import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { UserManagementService } from '../../services/user-management.service';
import { NotificationService } from '../../services/notification.service';
import { UsersApiActions, UsersPageActions, UsersMutationActions } from './users.actions';

@Injectable()
export class UsersEffects {
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(UserManagementService);
  private readonly notificationService = inject(NotificationService);

  readonly loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UsersPageActions.opened,
        UsersPageActions.filtersChanged,
        UsersPageActions.pageChanged,
      ),
      switchMap(() => this.userService.getAll().pipe(
        map((users) => UsersApiActions.loadSucceeded({ users })),
        catchError((error: { message?: string }) => of(
          UsersApiActions.loadFailed({ error: error?.message ?? 'Unable to load users' }),
        )),
      )),
    ),
  );

  readonly notifyLoadFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(UsersApiActions.loadFailed),
      tap(() => this.notificationService.error('USERS.ERROR_LOADING')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  readonly createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersMutationActions.createRequested),
      switchMap(({ requestId, request }) =>
        this.userService.create(request).pipe(
          map((user) => UsersMutationActions.createSucceeded({ requestId, user })),
          catchError((error: { message?: string }) =>
            of(UsersMutationActions.createFailed({
              requestId,
              error: error?.message ?? 'Unable to create user',
            })),
          ),
        ),
      ),
    ),
  );

  readonly notifyCreateSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(UsersMutationActions.createSucceeded),
      tap(() => this.notificationService.success('USERS.CREATE_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifyCreateFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(UsersMutationActions.createFailed),
      tap(() => this.notificationService.error('USERS.CREATE_ERROR')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Update
  // ---------------------------------------------------------------------------

  readonly updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersMutationActions.updateRequested),
      switchMap(({ requestId, request }) =>
        this.userService.update(request).pipe(
          map((user) => UsersMutationActions.updateSucceeded({ requestId, user })),
          catchError((error: { message?: string }) =>
            of(UsersMutationActions.updateFailed({
              requestId,
              error: error?.message ?? 'Unable to update user',
            })),
          ),
        ),
      ),
    ),
  );

  readonly notifyUpdateSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(UsersMutationActions.updateSucceeded),
      tap(() => this.notificationService.success('USERS.UPDATE_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifyUpdateFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(UsersMutationActions.updateFailed),
      tap(() => this.notificationService.error('USERS.UPDATE_ERROR')),
    ),
    { dispatch: false },
  );

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  readonly deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersMutationActions.deleteRequested),
      switchMap(({ requestId, id }) =>
        this.userService.delete(id).pipe(
          map(() => UsersMutationActions.deleteSucceeded({ requestId, id })),
          catchError((error: { message?: string }) =>
            of(UsersMutationActions.deleteFailed({
              requestId,
              error: error?.message ?? 'Unable to delete user',
            })),
          ),
        ),
      ),
    ),
  );

  readonly notifyDeleteSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(UsersMutationActions.deleteSucceeded),
      tap(() => this.notificationService.success('USERS.DELETE_SUCCESS')),
    ),
    { dispatch: false },
  );

  readonly notifyDeleteFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(UsersMutationActions.deleteFailed),
      tap(() => this.notificationService.error('USERS.DELETE_ERROR')),
    ),
    { dispatch: false },
  );
}

